import { useCallback, useMemo, useRef } from 'react'
import { useDataSource } from '../../../../../lib/dataSource'
import type { Task } from '../../../../../lib/db/models'
import { useListedChildren } from '../../../hooks/useListedChildren'
import { parentKeepsChildListed } from '../../../../../lib/db/taskFilters'
import { useOptimisticTaskOverrides } from '../../../hooks/useOptimisticTaskOverrides'
import {
  addMonths,
  buildMonthBuckets,
  filterUpcomingTasks,
  startOfMonth,
  type MonthBucket,
} from './upcomingTasks.utils'

interface UseUpcomingTasksArgs {
  lifeSpace: 'personal' | 'work' | 'home'
  startMonth: Date
  endMonth: Date
  graceIds: Set<string>
  /** True when the containing page is the active tab. While false, the
   *  tasks subscription is paused so unrelated writes don't re-fire the
   *  range scan on an invisible UpcomingPage. */
  isActive: boolean
}

/**
 * Reads tasks + projects from the data source, filters to the given life
 * space, and builds `MonthBucket[]` for the requested range. Re-computes on
 * writes and whenever `graceIds` changes (so just-completed tasks disappear
 * after their grace window).
 *
 * Query strategy (Dexie source): two index-scoped range queries on
 * `startDate` and `dueDate`, merged. Tasks outside the rendered month range
 * are never pulled into memory.
 */
export function useUpcomingTasks({
  lifeSpace,
  startMonth,
  endMonth,
  graceIds,
  isActive,
}: UseUpcomingTasksArgs): {
  months: MonthBucket[]
  isLoading: boolean
  /** Optimistically apply a partial task update so the change is visible
   *  in the next paint, instead of waiting on `db.updateTask` →
   *  LiveQuery → re-render (~1–2s with sync cycles in flight). Used for
   *  drag-and-drop (date change) AND badge updates (attention, duration,
   *  tags, etc.). The override is auto-cleared key-by-key as soon as the
   *  underlying Dexie data — checked BEFORE the override is re-applied —
   *  matches every key in the partial. */
  setOptimisticTaskOverride: (taskId: string, partial: Partial<Task>) => void
} {
  const source = useDataSource()
  const projects = source.useLifeSpaceProjects(lifeSpace)

  const lifeSpaceProjectIds = useMemo(
    () => new Set((projects ?? []).map(p => p.id)),
    [projects],
  )

  // Inclusive upper bound = first day of the month AFTER endMonth, so the
  // half-open range captures every day of endMonth itself.
  const rangeStart = startOfMonth(startMonth)
  const rangeEnd = addMonths(startOfMonth(endMonth), 1)

  // Tasks that start — or without a start are due — in the visible range.
  // Tasks outside it are never pulled into memory; unchanged tasks keep their
  // reference, so the memo chain below holds (see `DataSource`).
  // Raw from the source — does NOT incorporate optimistic overrides. Drives
  // the override-cleanup heuristics in the shared hook below (compared
  // against raw values, not against override-applied values, so cleanup
  // never snaps the UI back to its old state mid-flight).
  const rawTasks = source.useTasksInDateRange(rangeStart, rangeEnd, isActive)

  // Optimistic per-task partial overrides via the shared hook (object
  // deep-equal cleanup, hard timeout, updatedAt-advance early-clear).
  const { optimisticTaskOverrides, setOptimisticTaskOverride } =
    useOptimisticTaskOverrides(rawTasks)

  const tasks: Task[] | undefined = useMemo(() => {
    if (rawTasks === undefined) return undefined
    if (optimisticTaskOverrides.size === 0) return rawTasks
    return rawTasks.map(t => {
      const override = optimisticTaskOverrides.get(t.id)
      if (!override) return t
      return { ...t, ...override }
    })
  }, [rawTasks, optimisticTaskOverrides])

  // Hold the previous bucket result so buildMonthBuckets can ref-reuse
  // unchanged days/months. Without this, every Dexie write spawns fresh
  // refs all the way down → MonthBlock/DayGroup/TaskEntry memos all miss
  // → 2-second cascade re-render on every task mutation.
  /*
    **Unteraufgaben mit eigenem Datum** stehen hier (T14 #179) — mit dem Titel ihrer
    Aufgabe davor, und nur solange die Aufgabe selbst offen, wach und da ist: Sie ruhen,
    enden und gehen mit ihr (Grill 5 und 7). Ein gerade abgehaktes Kind bleibt in seiner
    Liegezeit stehen, auch wenn seine Aufgabe es mitgenommen hat.
  */
  // In der Liegezeit bleibt ein gerade (mit-)abgehaktes Kind stehen.
  const keep = useCallback((child: Task, parent: Task) => parentKeepsChildListed(parent)
    || (graceIds.has(child.id) && !parent.deletedAt && parent.pausedAt == null), [graceIds])
  const listed: Task[] | undefined = useListedChildren(tasks, keep)

  const prevBucketsRef = useRef<MonthBucket[]>([])
  const months = useMemo(() => {
    if (!listed || !projects) return prevBucketsRef.current
    const filtered = filterUpcomingTasks({
      tasks: listed,
      lifeSpace,
      lifeSpaceProjectIds,
      graceIds,
    })
    const next = buildMonthBuckets(filtered, startMonth, endMonth, prevBucketsRef.current)
    prevBucketsRef.current = next
    return next
  }, [listed, projects, lifeSpace, lifeSpaceProjectIds, graceIds, startMonth, endMonth])

  return {
    months,
    isLoading: listed === undefined || projects === undefined,
    setOptimisticTaskOverride,
  }
}
